import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import { createAction, createReducer } from 'redux-act';

import {
  SravniNewsSharedEnumsDictionaryType,
  SravniNewsSharedEnumsMaterialType,
  SravniNewsSharedEnumsPlacementType,
} from '../__generated__/news/Api';
import { SUBDOMAIN_RUBRICS_PARAMS } from '../constants/news';
import {
  IAdditionalReadingQuery,
  IDictionaryDto,
  IHighlightedMaterial,
  IMaterial,
  IMaterialsListQuery,
  IOneMoreArticleQuery,
  IPartnersMaterialsQuery,
  IRubricResultModel,
  TAdditionalReading,
  TAuthorShort,
  TAuthorsQuery,
  TErrorModel,
  THighLight,
  TMaterialHighlightsList,
  TMaterialHighlightsListQuery,
  TMaterialListQuery,
  TMaterialSingleQuery,
  TMaterialsPopularQuery,
  TPartnerMaterial,
  TPlacementConfigurationShort,
  TPlacementConfigurationShortMap,
  TRubricsListQuery,
  TRubricTagsQuery,
  TTag,
} from '../interfaces/news';
import {
  getAuthors,
  getDictionaries,
  getPlacements,
  getRubrics,
  selectHighlightsList,
  selectPartnerMaterials,
  selectPopularMaterials,
  selectTagsByRubric,
} from '../selectors/news';
import {
  getAdditionalReading,
  getAllPlacements,
  getAuthorsList,
  getDictionary,
  getHighlightsList,
  getMaterialByAlias,
  getMaterialsList,
  getOneMoreArticle,
  getPartnerMaterials,
  getPopularHighlights,
  getPopularMaterials,
  getRubricsList,
  getTagsByRubric,
} from '../services/news';
import { App } from '../types';
import { isDevBuild } from '../utils/environment';

import { IGlobalState } from './index';

export interface IState {
  list: {
    data: IHighlightedMaterial[];
    loading: boolean;
    error: TErrorModel;
    listForUrl?: string;
    canLoadingMore: boolean;
  };
  material: {
    data: IMaterial[];
    currentArticleIndex: number;
    loading: boolean;
    error: TErrorModel;
  };
  latestNews: {
    data: {
      list: IHighlightedMaterial[];
      mainItem: IHighlightedMaterial;
    };

    loading: boolean;
    error: TErrorModel;
  };
  partnerMaterials: {
    data: {
      //key is alias
      [key: string]: TPartnerMaterial;
    };
    loading: boolean;
    error: TErrorModel;
  };
  additionalReading: {
    data: {
      //key is alias
      [key: string]: TAdditionalReading;
    };
    loading: boolean;
    error: TErrorModel;
  };
  placements: {
    data: TPlacementConfigurationShortMap;
    loading: boolean;
    error: TErrorModel;
  };
  // эти рубрики используются только для вывода бокового меню
  rubrics: {
    data: IRubricResultModel[];
    loading: boolean;
    error: TErrorModel;
  };
  rubricsHasSubDomain: {
    data: string[];
    loading: boolean;
    error: TErrorModel;
  };
  tags: {
    data: {
      [key: string]: TTag[];
    };
    loading: boolean;
    error: TErrorModel;
  };
  // здесь весь набор рубрик тэгов и протуктовых тегов их нужно использовать для мапинга
  dictionaries: {
    data: {
      [key in SravniNewsSharedEnumsDictionaryType]?: IDictionaryDto[];
    };
    loading: boolean;
    error: TErrorModel;
  };
  meta: {
    articles: IMaterial['id'][];
  };
  authors: {
    data: TAuthorShort[];
    loading: boolean;
    error: TErrorModel;
  };
  popular: {
    data: THighLight[];
    loading: boolean;
    error: TErrorModel;
  };
  highlightsList: {
    data: TMaterialHighlightsList;
    loading: boolean;
    error: TErrorModel;
  };
  viewedArticles: number[];
  articlesWithReadyToShowComments: number[];
  articleWithCommentsUserWantsToSee: number[];
}

const initialState: IState = {
  list: {
    data: [],
    loading: false,
    error: null,
    canLoadingMore: true,
  },
  material: {
    data: [],
    currentArticleIndex: 0,
    loading: false,
    error: null,
  },
  latestNews: {
    data: {
      list: [],
      mainItem: null,
    },
    loading: false,
    error: null,
  },
  partnerMaterials: {
    data: {},
    loading: false,
    error: null,
  },
  additionalReading: {
    data: {},
    loading: false,
    error: null,
  },
  placements: {
    data: {},
    loading: false,
    error: null,
  },
  rubrics: {
    data: [],
    loading: false,
    error: null,
  },
  rubricsHasSubDomain: {
    data: [],
    loading: false,
    error: null,
  },
  tags: {
    data: {},
    loading: false,
    error: null,
  },
  dictionaries: {
    data: {},
    loading: false,
    error: null,
  },
  meta: {
    articles: [],
  },
  authors: {
    data: [],
    loading: false,
    error: null,
  },
  popular: {
    data: [],
    loading: false,
    error: null,
  },
  highlightsList: {
    data: {},
    loading: false,
    error: null,
  },
  viewedArticles: [],
  articlesWithReadyToShowComments: [],
  articleWithCommentsUserWantsToSee: [],
};

const convertMaterialsToMapByAlias = <T>(materials: { [key: string]: any; alias?: string }[]) => {
  return materials.reduce((acc, material) => {
    acc[material.alias] = material;
    return acc;
  }, {}) as T;
};
export const startLoadingLatestNews = createAction('news/latestList/loading');
export const loadLatestNewsSuccess = createAction<{ list: IHighlightedMaterial[]; mainItem: IHighlightedMaterial }>(
  'news/latestList/success',
);
export const loadLatestNewsFail = createAction<TErrorModel>('news/latestList/fail');

export const getLatestNews = async (params: TMaterialListQuery, isPrivate?: boolean) => {
  let [list, popularList] = await Promise.all([
    getMaterialsList({
      Placement: SravniNewsSharedEnumsPlacementType.NewsList,
      Take: 6,
      Skip: 0,
      //@ts-ignore
      type: isPrivate ? SravniNewsSharedEnumsMaterialType.PrivateNews : SravniNewsSharedEnumsMaterialType.News,
      DateFrom: new Date().toISOString(),
      ...params,
    }),
    getPopularHighlights({
      Rubrics: params.Rubrics,
      Limit: 1,
    }),
  ]);

  const popularItem = popularList?.items?.[0];
  const popularItemIndex = list?.findIndex((item) => item.id === popularItem?.id);

  if (popularItemIndex > -1) list?.splice(popularItemIndex, 1);
  else list = list?.slice(0, 5);

  return {
    list: list,
    mainItem: popularItem,
  };
};

export const fetchLatestNews = (params: TMaterialListQuery = {}): App.ReduxAction => {
  return async (dispatch) => {
    try {
      dispatch(startLoadingLatestNews());

      const data = await getLatestNews({
        ...params,
        Rubrics: ['novosti'],
      });

      dispatch(loadLatestNewsSuccess(data));
    } catch (error) {
      dispatch(loadLatestNewsFail(error));
    }
  };
};

export const startLoadingMaterialsList = createAction<{ isNew: boolean }>('news/list/loading');
export const loadMaterialsListSuccess = createAction<{ list: IHighlightedMaterial[]; isNew: boolean }>(
  'news/list/success',
);
export const loadMaterialsListFail = createAction<TErrorModel>('news/list/fail');

export const fetchMaterialsList = (params: IMaterialsListQuery, isNew?: boolean): App.ReduxAction => {
  return async (dispatch) => {
    try {
      dispatch(startLoadingMaterialsList({ isNew }));

      const list = await getMaterialsList(params);

      dispatch(loadMaterialsListSuccess({ list, isNew }));
      if (list.length === 0) {
        dispatch(setCanLoadingMore(false));
      }
    } catch (error) {
      dispatch(loadMaterialsListFail(error));
    }
  };
};

export const filterPlacementsByTagsAndRubrics = (
  placementMaterials: TPlacementConfigurationShort['materials'],
  { Rubrics, Tags }: Pick<IMaterialsListQuery, 'Rubrics' | 'Tags'>,
) => {
  let matchedPlacementMaterials = placementMaterials;
  if (Rubrics?.length) {
    matchedPlacementMaterials = matchedPlacementMaterials.filter(({ rubric }) => {
      return Rubrics.includes(rubric as IMaterialsListQuery['Rubrics'][number]);
    });
  }
  if (Tags?.length) {
    matchedPlacementMaterials = matchedPlacementMaterials.filter(({ tags }) => {
      return !!intersection(tags, Tags)?.length;
    });
  }
  return matchedPlacementMaterials;
};

export const fetchFirstBatchForMaterialsList = (params: IMaterialsListQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const { Rubrics, Tags } = params;
    const { placements } = getState().news;
    const { maxItems = 0, materials: placementMaterials = [] } = placements.data?.[params.Placement] || {};

    const matchedPlacementMaterials = filterPlacementsByTagsAndRubrics(placementMaterials, { Rubrics, Tags });

    const placementMaterialsIds = matchedPlacementMaterials.map(({ id }) => id);
    if (placementMaterialsIds.length) {
      params.placementId = placementMaterialsIds.slice(0, maxItems);
    }
    await dispatch(fetchMaterialsList(params, true));
  };
};

export const fetchMoreItemsForMaterialsList = (params: IMaterialsListQuery, isNew?: boolean): App.ReduxAction => {
  return async (dispatch, getState) => {
    const {
      meta,
      placements,
      list: { data: list },
    } = getState().news;
    const { Rubrics, Tags } = params;
    const { maxItems = 0, materials: placementMaterials = [] } = placements.data?.[params.Placement] || {};

    const matchedPlacementMaterials = filterPlacementsByTagsAndRubrics(placementMaterials, { Rubrics, Tags });

    const placementMaterialsIds = matchedPlacementMaterials?.map(({ id }) => id);
    // проверяем что кол-во мест для отображаемых плеййсментов еще не использованно
    const downloadedMaterialsId = list.map(({ id }) => id);
    const downloadedPlacements = intersection(downloadedMaterialsId, placementMaterialsIds);
    const otherPlacements = placementMaterialsIds.filter((placement) => !downloadedPlacements.includes(placement));

    if (maxItems >= downloadedPlacements.length) {
      params.placementId = otherPlacements?.slice(0, maxItems - downloadedPlacements.length);
    }
    params.ExcludedIds = meta.articles;
    await dispatch(fetchMaterialsList(params, isNew));
  };
};

export const startLoadingMaterialSingle = createAction('news/single/loading');
export const loadMaterialsSingleSuccess = createAction<{ article: IMaterial; isNew?: boolean }>('news/single/success');
export const loadMaterialsSingleFail = createAction<TErrorModel>('news/single/fail');
export const setCurrentArticleIndex = createAction<string>('news/single/currentArticleIndex/set');

export const selectMaterialByAlias = (state: IGlobalState) => (alias: string) => {
  return state.news.material.data?.find((item) => item.alias === alias);
};

export const selectPlacementEndlessScroll = (state: IGlobalState) => (alias: string) => {
  return state.news.placements.data?.endlessScroll.materials.find((item) => item.alias === alias);
};

export const fetchMaterialSingle = (params: TMaterialSingleQuery, isNew?: boolean): App.ReduxAction => {
  return async (dispatch) => {
    try {
      dispatch(startLoadingMaterialSingle());
      const article = await getMaterialByAlias(params);
      dispatch(loadMaterialsSingleSuccess({ article, isNew }));
    } catch (error) {
      dispatch(loadMaterialsSingleFail(error));
    }
  };
};

export const fetchOneMoreArticle = (params: IOneMoreArticleQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const { Rubrics, Placement, isPartnerNews, excludeExternalsFromPlacements } = params;
    const {
      meta,
      placements,
      material: { data: materials },
      popular,
    } = getState().news;

    let loadPlacement = false;
    let loadPopularList = false;
    let nextPopularMaterial;

    const downloadedMaterialsId = meta.articles;
    const placementMaterials = placements.data?.endlessScroll?.materials;

    const requestParams = {
      ...params,
      ExcludedIds: downloadedMaterialsId,
    };

    // если есть плейсменты, то ищем среди них подходящий
    if (!isPartnerNews && Placement && placementMaterials?.length) {
      // const maxItems = placements.data?.endlessScroll?.maxItems;
      const maxItems = 1; // temporary value

      const targetPlacement = placementMaterials?.find(
        ({ isTargetPlacement, rubric, placementProductTags, placementTags, externalLink }) => {
          if (!isTargetPlacement || (excludeExternalsFromPlacements && Boolean(externalLink))) return false;

          const firstMaterial = materials[0];
          const matchRubric = rubric === firstMaterial.rubric;
          const matchProductTags = firstMaterial.productTags.find((tag) => placementProductTags?.includes(tag));
          const matchTags = firstMaterial.tags.find((tag) => placementTags?.includes(tag));

          return matchRubric || matchProductTags || matchTags;
        },
      );

      if (targetPlacement) {
        const placementIds = placementMaterials.map((p) => p.id);
        const downloadedPlacementsCount = intersection(downloadedMaterialsId, placementIds).length;

        if (downloadedPlacementsCount < maxItems && !downloadedMaterialsId.includes(targetPlacement.id)) {
          loadPlacement = true;
          requestParams.placementId = targetPlacement.id;
        }
      }
    }

    // если нет плейсментов или не нашли подходящий, то грузим популярные
    if (!loadPlacement) {
      loadPopularList = !Rubrics || (Rubrics?.includes('novosti') && Rubrics.length === 1);

      if (loadPopularList) {
        nextPopularMaterial = popular.data.find(
          ({ id, isExternal }) => !downloadedMaterialsId.includes(id) && !isExternal,
        );
      }
    }

    try {
      let article;
      dispatch(startLoadingMaterialSingle);

      if (loadPlacement || !loadPopularList) {
        article = await getOneMoreArticle(requestParams);
      } else if (nextPopularMaterial) {
        article = await getMaterialByAlias({ Alias: nextPopularMaterial.alias });
      }

      if (article) dispatch(loadMaterialsSingleSuccess({ article }));
    } catch (error) {
      dispatch(loadMaterialsSingleFail(error));
    }
  };
};

export const startLoadingAllPlacements = createAction('news/allPlacements/loading');
export const loadAllPlacementsSuccess = createAction<TPlacementConfigurationShortMap>('news/allPlacements/success');
export const loadAllPlacementsFail = createAction<TErrorModel>('news/allPlacements/fail');

export const fetchAllPlacementsShortIfNeeded = (): App.ReduxAction => {
  return async (dispatch, getState) => {
    const placementsMap = getPlacements(getState());
    if (isEmpty(placementsMap)) {
      try {
        dispatch(startLoadingAllPlacements());
        const placements = await getAllPlacements();
        dispatch(loadAllPlacementsSuccess(placements));
      } catch (error) {
        dispatch(loadAllPlacementsFail(error));
      }
    }
  };
};

export const startLoadingRubricsList = createAction('news/rubrics/loading');
export const loadRubricsListSuccess = createAction<IRubricResultModel[]>('news/rubrics/success');
export const loadRubricsListFail = createAction<TErrorModel>('news/rubrics/fail');

export const startLoadingRubricsHasSubDomainList = createAction('news/rubricsHasSubDomain/loading');
export const loadRubricsHasSubDomainListSuccess = createAction<string[]>('news/rubricsHasSubDomain/success');
export const loadRubricsHasSubDomainListFail = createAction<TErrorModel>('news/rubricsHasSubDomain/fail');

export const fetchRubricsListIfNeeded = (params?: TRubricsListQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const rubrics = getRubrics(getState());
    if (!rubrics?.length) {
      try {
        dispatch(startLoadingRubricsList());
        const { items } = await getRubricsList(params);
        dispatch(loadRubricsListSuccess(items));
      } catch (error) {
        dispatch(loadRubricsListFail(error));
      }
    }
  };
};

export const fetchRubricsHasSubDomainListIfNeeded = (params?: TRubricsListQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const rubrics = getRubrics(getState());
    if (!rubrics?.length) {
      try {
        dispatch(startLoadingRubricsHasSubDomainList());
        const { items } = await getRubricsList(params);
        const filteredDictionaries = items.reduce(
          (acc, rubric) => (rubric.subDomain.length !== 0 ? [...acc, rubric.alias] : acc),
          [],
        );
        dispatch(loadRubricsHasSubDomainListSuccess(filteredDictionaries));
      } catch (error) {
        dispatch(loadRubricsHasSubDomainListFail(error));
      }
    }
  };
};

export const startLoadingTags = createAction('news/tags/loading');
export const loadTagsSuccess = createAction<{ rubric: string; data: TTag[] }>('news/tags/success');
export const loadTagsFail = createAction<TErrorModel>('news/tags/fail');

export const fetchTagsByRubricIfNeeded = (params: TRubricTagsQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const tags = selectTagsByRubric(params.rubric)(getState());
    if (!tags?.length) {
      try {
        dispatch(startLoadingTags());
        const data = await getTagsByRubric(params);
        dispatch(loadTagsSuccess({ rubric: params.rubric, data }));
      } catch (error) {
        dispatch(loadTagsFail(error));
      }
    }
  };
};

export const startLoadingDictionary = createAction('news/dictionary/loading');
export const loadDictionarySuccess = createAction<IState['dictionaries']['data']>('news/dictionary/success');
export const loadDictionaryFail = createAction<TErrorModel>('news/dictionary/fail');

export const fetchDictionary = (): App.ReduxAction => {
  return async (dispatch) => {
    try {
      dispatch(startLoadingRubricsHasSubDomainList());

      dispatch(startLoadingDictionary());
      const dictionaries = await getDictionary();

      const { items } = await getRubricsList(SUBDOMAIN_RUBRICS_PARAMS);
      const subDomainAliases = items.reduce(
        (acc, rubric) => (rubric.subDomain.length !== 0 ? [...acc, rubric.alias] : acc),
        [],
      );

      let filteredHasSubDomainDictionaries = {
        ...dictionaries,
        rubrics: dictionaries.rubrics.filter((rubric) => !subDomainAliases.includes(rubric.alias)),
      };

      dispatch(loadDictionarySuccess(filteredHasSubDomainDictionaries));
    } catch (error) {
      dispatch(loadDictionaryFail(error));
    }
  };
};

export const fetchAllDictionariesIfNeeded = (): App.ReduxAction => {
  return async (dispatch, getState) => {
    const dictionaries = getDictionaries(getState());
    if (isEmpty(dictionaries)) {
      await dispatch(fetchDictionary());
    }
  };
};

export const startLoadingPartnerMaterials = createAction('news/partnerMaterials/loading');
export const loadPartnerMaterialForListSuccess = createAction<TPartnerMaterial[]>(
  'news/partnerMaterialsForMainPages/success',
);
export const loadPartnerMaterialsSuccess = createAction<{ partnerMaterials: TPartnerMaterial[]; articleAlias: string }>(
  'news/partnerMaterials/success',
);
export const loadPartnerMaterialsFail = createAction<TErrorModel>('news/partnerMaterials/fail');
export const removeExtraMaterials = createAction('news/removeExtraMaterials/remove');

export const fetchPartnerMaterials = (params: IPartnersMaterialsQuery, articleAlias: string): App.ReduxAction => {
  return async (dispatch, getState) => {
    const {
      meta,
      placements,
      partnerMaterials: { data },
    } = getState().news;
    const maxItems = params.Take;
    const { materials: placementMaterials = [] } = placements.data?.partnerMaterials || {};
    const placementMaterialsIds = placementMaterials.map(({ id }) => id);
    // проверяем что кол-во мест для отображаемых плеййсментов еще не использованно
    const downloadedPartnerMaterialsId = Object.values(data).map(({ id }) => id);
    const downloadedPlacements = intersection(downloadedPartnerMaterialsId, placementMaterialsIds);
    const otherPlacements = placementMaterialsIds.filter((placement) => !downloadedPlacements.includes(placement));

    if (maxItems >= downloadedPlacements.length) {
      params.placementId = otherPlacements?.slice(0, maxItems - downloadedPlacements.length);
    }
    params.ExcludedIds = meta.articles;
    try {
      dispatch(startLoadingPartnerMaterials());
      const partnerMaterials = await getPartnerMaterials(params);
      dispatch(loadPartnerMaterialsSuccess({ partnerMaterials, articleAlias }));
    } catch (error) {
      dispatch(loadPartnerMaterialsFail(error));
    }
  };
};

export const fetchPartnerMaterialsIfNeeded = (
  params: IPartnersMaterialsQuery,
  articleAlias: string,
): App.ReduxAction => {
  return (dispatch, getState) => {
    const article = selectMaterialByAlias(getState())(articleAlias);
    if (!article?.partnerMaterials) {
      return dispatch(fetchPartnerMaterials(params, articleAlias));
    }
  };
};

export const fetchPartnerMaterialsForListIfNeeded = (params: IPartnersMaterialsQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const partnerMaterials = selectPartnerMaterials(getState());
    if (isEmpty(partnerMaterials) || Object.keys(partnerMaterials).length < 3) {
      try {
        dispatch(startLoadingPartnerMaterials());
        const materials = await getPartnerMaterials(params);
        dispatch(loadPartnerMaterialForListSuccess(materials));
      } catch (error) {
        dispatch(loadPartnerMaterialsFail(error));
      }
    }
  };
};

export const startLoadingAdditionalReading = createAction('news/additionalReading/loading');
export const loadAdditionalReadingSuccess = createAction<{
  additionalReading: TAdditionalReading[];
  articleAlias: string;
}>('news/additionalReading/success');
export const loadAdditionalReadingFail = createAction<TErrorModel>('news/additionalReading/fail');

export const fetchAdditionalReading = (params: IAdditionalReadingQuery, articleAlias: string): App.ReduxAction => {
  return async (dispatch, getState) => {
    const {
      meta,
      placements,
      additionalReading: { data },
    } = getState().news;
    const { maxItems = 0, materials: placementMaterials = [] } = placements.data?.additionalReading || {};
    const placementMaterialsIds = placementMaterials.map(({ id }) => id);
    // проверяем что кол-во мест для отображаемых плеййсментов еще не использованно
    const downloadedAdditionalReadingId = Object.values(data).map(({ id }) => id);
    const downloadedPlacements = intersection(downloadedAdditionalReadingId, placementMaterialsIds);
    const otherPlacements = placementMaterialsIds.filter((placement) => !downloadedPlacements.includes(placement));

    if (maxItems >= downloadedPlacements.length) {
      params.placementId = otherPlacements?.slice(0, maxItems - downloadedPlacements.length);
    }
    params.ExcludedIds = meta.articles;
    try {
      dispatch(startLoadingAdditionalReading());
      const additionalReading = await getAdditionalReading(params);
      dispatch(loadAdditionalReadingSuccess({ additionalReading, articleAlias }));
    } catch (error) {
      dispatch(loadAdditionalReadingFail(error));
    }
  };
};

export const fetchAdditionalReadingIfNeeded = (
  params: IAdditionalReadingQuery,
  articleAlias: string,
  isNew?: boolean,
): App.ReduxAction => {
  return (dispatch, getState) => {
    const article = selectMaterialByAlias(getState())(articleAlias);
    if (!article?.additionalReading) {
      if (isNew) {
        dispatch(removeExtraMaterials());
      }
      return dispatch(fetchAdditionalReading(params, articleAlias));
    }
  };
};

export const startLoadingAuthorsList = createAction('news/authors/loading');
export const loadAuthorsListSuccess = createAction<TAuthorShort[]>('news/authors/success');
export const loadAuthorsListFail = createAction<TErrorModel>('news/authors/fail');

export const fetchAuthorsListIfNeeded = (params?: TAuthorsQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const authors = getAuthors(getState());
    if (!authors?.length) {
      try {
        dispatch(startLoadingAuthorsList());
        const items = await getAuthorsList(params);
        dispatch(loadAuthorsListSuccess(items));
      } catch (error) {
        dispatch(loadAuthorsListFail(error));
      }
    }
  };
};

export const startLoadingPopularMaterials = createAction('news/popular/loading');
export const loadPopularMaterialsSuccess = createAction<THighLight[]>('news/popular/success');
export const loadPopularMaterialsFail = createAction<TErrorModel>('news/popular/fail');

export const fetchPopularMaterialsIfNeeded = (params: TMaterialsPopularQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const list = selectPopularMaterials(getState());
    if (!list?.length) {
      try {
        dispatch(startLoadingPopularMaterials());
        const { items } = await getPopularMaterials(params);
        dispatch(loadPopularMaterialsSuccess(items));
      } catch (error) {
        dispatch(loadPopularMaterialsFail(error));
      }
    }
  };
};

export const startLoadingHighlightsList = createAction('news/highlightsList/loading');
export const loadHighlightsListSuccess = createAction<TMaterialHighlightsList>('news/highlightsList/success');
export const loadHighlightsListFail = createAction<TErrorModel>('news/highlightsList/fail');

export const fetchHighlightsListIfNeeded = (params: TMaterialHighlightsListQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const list = selectHighlightsList(getState());
    if (!list?.items?.length) {
      try {
        dispatch(startLoadingHighlightsList());
        const data = await getHighlightsList(params);
        dispatch(loadHighlightsListSuccess(data));
      } catch (error) {
        dispatch(loadHighlightsListFail(error));
      }
    }
  };
};

export const addViewedArticle = createAction<number>('news/viewedArticles/add');
export const resetViewedArticle = createAction('news/viewedArticles/reset');
export const addArticleWithReadyToShowComments = createAction<number>('news/articlesWithReadyToShowComments/add');
export const resetArticlesWithReadyToShowComments = createAction('news/articlesWithReadyToShowComments/reset');
export const addArticleWithCommentsUserWantsToSee = createAction<number>('news/articleWithCommentsUserWantsToSee/add');
export const setForListLoad = createAction<string>('news/forList');
export const setCanLoadingMore = createAction<boolean>('news/list/canLoadingMore');

const news = createReducer<IState>({}, initialState)
  .on(setCanLoadingMore, (state, canLoadingMore) => {
    return {
      ...state,
      list: {
        ...state.list,
        canLoadingMore,
      },
    };
  })
  .on(setForListLoad, (state, url) => {
    return {
      ...state,
      list: {
        ...state.list,
        listForUrl: url,
      },
    };
  })
  .on(startLoadingMaterialsList, (state, { isNew }) => {
    return {
      ...state,
      list: {
        ...state.list,
        data: isNew ? [] : state.list.data,
        loading: true,
      },
    };
  })
  .on(loadMaterialsListSuccess, (state, { list: highlights, isNew }) => {
    return {
      ...state,
      list: {
        ...state.list,
        data: isNew ? highlights : [...state.list.data, ...highlights],
        loading: false,
      },
      meta: {
        ...state.meta,
        articles: isNew
          ? highlights?.map(({ id }) => id)
          : [...new Set([...state.meta.articles, ...highlights?.map(({ id }) => id)])],
      },
    };
  })
  .on(loadMaterialsListFail, (state, error) => {
    return {
      ...state,
      list: {
        ...state.list,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingMaterialSingle, (state) => {
    return {
      ...state,
      material: {
        ...state.material,
        loading: true,
      },
    };
  })
  .on(loadMaterialsSingleSuccess, (state, { article, isNew }) => {
    if (isEmpty(article)) return state;
    return {
      ...state,
      material: {
        ...state.material,
        data: isNew ? [article] : [...state.material.data, article],
        ...(isNew && { currentArticleIndex: 0 }),
        loading: false,
        error: null,
      },
      meta: {
        ...state.meta,
        articles: isNew ? [article.id] : [...new Set([...state.meta.articles, article.id])],
      },
    };
  })
  .on(loadMaterialsSingleFail, (state, error) => {
    return {
      ...state,
      material: {
        ...state.material,
        error,
        loading: false,
      },
    };
  })
  .on(setCurrentArticleIndex, (state, alias) => {
    const {
      material: { data: materials, currentArticleIndex },
    } = state;

    const articleIndex = materials.findIndex((material) => material.alias === alias);
    return {
      ...state,
      material: {
        ...state.material,
        currentArticleIndex: articleIndex > -1 ? articleIndex : currentArticleIndex,
      },
    };
  })

  .on(startLoadingAllPlacements, (state) => {
    return {
      ...state,
      placements: {
        ...state.placements,
        loading: true,
      },
    };
  })
  .on(loadAllPlacementsSuccess, (state, placements) => {
    return {
      ...state,
      placements: {
        ...state.placements,
        loading: false,
        data: {
          ...placements,
          ...(isDevBuild && {
            [SravniNewsSharedEnumsPlacementType.EndlessScroll]: {
              ...placements.endlessScroll,
              maxItems: 1,
            },
          }),
        },
      },
    };
  })
  .on(loadAllPlacementsFail, (state, error) => {
    return {
      ...state,
      placements: {
        ...state.placements,
        loading: false,
        error,
      },
    };
  })

  .on(startLoadingRubricsList, (state) => {
    return {
      ...state,
      rubrics: {
        ...state.rubrics,
        loading: true,
      },
    };
  })
  .on(loadRubricsListSuccess, (state, payload) => {
    return {
      ...state,
      rubrics: {
        ...state.rubrics,
        data: payload,
        loading: false,
      },
    };
  })
  .on(loadRubricsListFail, (state, error) => {
    return {
      ...state,
      rubrics: {
        ...state.rubrics,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingRubricsHasSubDomainList, (state) => {
    return {
      ...state,
      rubricsHasSubDomain: {
        ...state.rubricsHasSubDomain,
        loading: true,
      },
    };
  })
  .on(loadRubricsHasSubDomainListSuccess, (state, payload) => {
    return {
      ...state,
      rubricsHasSubDomain: {
        ...state.rubricsHasSubDomain,
        data: payload,
        loading: false,
      },
    };
  })
  .on(loadRubricsHasSubDomainListFail, (state, error) => {
    return {
      ...state,
      rubricsHasSubDomain: {
        ...state.rubricsHasSubDomain,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingTags, (state) => {
    return {
      ...state,
      tags: {
        ...state.tags,
        loading: true,
      },
    };
  })
  .on(loadTagsSuccess, (state, { rubric, data }) => {
    return {
      ...state,
      tags: {
        ...state.tags,
        data: {
          ...state.tags.data,
          [rubric]: data,
        },
        loading: false,
      },
    };
  })
  .on(loadTagsFail, (state, error) => {
    return {
      ...state,
      tags: {
        ...state.tags,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingDictionary, (state) => {
    return {
      ...state,
      dictionaries: {
        ...state.dictionaries,
        loading: true,
      },
    };
  })
  .on(loadDictionarySuccess, (state, payload) => {
    return {
      ...state,
      dictionaries: {
        ...state.dictionaries,
        data: {
          ...state.dictionaries.data,
          ...payload,
        },
        loading: false,
      },
    };
  })
  .on(loadDictionaryFail, (state, error) => {
    return {
      ...state,
      dictionaries: {
        ...state.dictionaries,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingPartnerMaterials, (state) => {
    return {
      ...state,
      partnerMaterials: {
        ...state.partnerMaterials,
        loading: true,
      },
    };
  })
  .on(loadPartnerMaterialForListSuccess, (state, partnerMaterials) => {
    const materialsMap = convertMaterialsToMapByAlias<IState['partnerMaterials']['data']>(partnerMaterials);
    return {
      ...state,
      partnerMaterials: {
        data: materialsMap,
        loading: false,
        error: null,
      },
    };
  })
  .on(loadPartnerMaterialsSuccess, (state, { partnerMaterials, articleAlias }) => {
    const materialsMap = convertMaterialsToMapByAlias<IState['partnerMaterials']['data']>(partnerMaterials);

    return {
      ...state,
      partnerMaterials: {
        ...state.partnerMaterials,
        data: {
          ...state.partnerMaterials.data,
          ...materialsMap,
        },
        loading: false,
        error: null,
      },
      // обогощаем контракт IMaterial добавляем туда partnerMaterials
      material: {
        ...state.material,
        data: state.material.data.map((item) => {
          if (item.alias === articleAlias) {
            return {
              ...item,
              partnerMaterials: partnerMaterials.map(({ alias }) => alias),
            };
          }
          return item;
        }),
      },
      meta: {
        articles: [...new Set([...state.meta.articles, ...partnerMaterials.map(({ id }) => id)])],
      },
    };
  })
  .on(loadPartnerMaterialsFail, (state, error) => {
    return {
      ...state,
      partnerMaterials: {
        ...state.partnerMaterials,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingAdditionalReading, (state) => {
    return {
      ...state,
      additionalReading: {
        ...state.additionalReading,
        loading: true,
      },
    };
  })
  .on(loadAdditionalReadingSuccess, (state, { additionalReading, articleAlias }) => {
    const materialsMap = additionalReading.reduce<IState['additionalReading']['data']>((acc, material) => {
      acc[material.alias] = material;
      return acc;
    }, {});

    return {
      ...state,
      additionalReading: {
        ...state.additionalReading,
        data: {
          ...state.additionalReading.data,
          ...materialsMap,
        },
        loading: false,
        error: null,
      },
      // обогощаем контракт IMaterial добавляем туда additionalReading
      material: {
        ...state.material,
        data: state.material.data.map((item) => {
          if (item.alias === articleAlias) {
            return {
              ...item,
              additionalReading: additionalReading.map(({ alias }) => alias),
            };
          }
          return item;
        }),
      },
      meta: {
        articles: [...new Set([...state.meta.articles, ...additionalReading.map(({ id }) => id)])],
      },
    };
  })
  .on(loadAdditionalReadingFail, (state, error) => {
    return {
      ...state,
      additionalReading: {
        ...state.additionalReading,
        error,
        loading: false,
      },
    };
  })
  .on(removeExtraMaterials, (state) => {
    return {
      ...state,
      additionalReading: {
        ...state.additionalReading,
        data: {},
      },
      partnerMaterials: {
        ...state.partnerMaterials,
        data: {},
      },
    };
  })

  .on(startLoadingAuthorsList, (state) => {
    return {
      ...state,
      authors: {
        ...state.authors,
        loading: true,
      },
    };
  })
  .on(loadAuthorsListSuccess, (state, payload) => {
    return {
      ...state,
      authors: {
        ...state.authors,
        data: payload,
        loading: false,
      },
    };
  })
  .on(loadAuthorsListFail, (state, error) => {
    return {
      ...state,
      authors: {
        ...state.authors,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingPopularMaterials, (state) => {
    return {
      ...state,
      popular: {
        ...state.popular,
        loading: true,
      },
    };
  })
  .on(loadPopularMaterialsSuccess, (state, payload) => {
    return {
      ...state,
      popular: {
        ...state.popular,
        data: payload,
        loading: false,
      },
    };
  })
  .on(loadPopularMaterialsFail, (state, error) => {
    return {
      ...state,
      popular: {
        ...state.popular,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingLatestNews, (state) => ({
    ...state,
    latestNews: {
      ...state.latestNews,
      loading: true,
    },
  }))
  .on(loadLatestNewsSuccess, (state, data) => ({
    ...state,
    latestNews: {
      ...state.latestNews,
      data: {
        ...state.latestNews.data,
        list: data.list,
        mainItem: data.mainItem,
      },
      loading: false,
    },
  }))
  .on(loadLatestNewsFail, (state, error) => ({
    ...state,
    latestNews: {
      ...state.latestNews,
      error,
    },
  }))

  .on(startLoadingHighlightsList, (state) => {
    return {
      ...state,
      highlightsList: {
        ...state.highlightsList,
        loading: true,
      },
    };
  })
  .on(loadHighlightsListSuccess, (state, payload) => {
    return {
      ...state,
      highlightsList: {
        ...state.highlightsList,
        data: payload,
        loading: false,
      },
    };
  })
  .on(loadHighlightsListFail, (state, error) => {
    return {
      ...state,
      highlightsList: {
        ...state.highlightsList,
        error,
        loading: false,
      },
    };
  })
  .on(addViewedArticle, (state, payload) => {
    return {
      ...state,
      viewedArticles: [...state.viewedArticles, payload],
    };
  })
  .on(resetViewedArticle, (state) => {
    if (state.viewedArticles.length === 0) {
      return state;
    }
    return {
      ...state,
      viewedArticles: [],
    };
  })
  .on(addArticleWithReadyToShowComments, (state, payload) => {
    if (state.articlesWithReadyToShowComments.includes(payload)) return state;

    return {
      ...state,
      articlesWithReadyToShowComments: [...state.articlesWithReadyToShowComments, payload],
    };
  })
  .on(resetArticlesWithReadyToShowComments, (state) => {
    return {
      ...state,
      articlesWithReadyToShowComments: [],
    };
  })
  .on(addArticleWithCommentsUserWantsToSee, (state, payload) => {
    return {
      ...state,
      articleWithCommentsUserWantsToSee: [...state.articleWithCommentsUserWantsToSee, payload],
    };
  });
export default news;
